/**
 * Shared form submission views.
 *
 * Portal-agnostic: these take a plain DTO and render it, so Cascade, One Up
 * Admin, and the Client portal show submissions identically and a fix lands once.
 * Built from the shared vocabulary in record-presentation.tsx.
 *
 * Submitted fields render from the preserved label/value map rather than a fixed
 * schema, which keeps an old entry readable after the website form changes.
 */

import { useState, type ReactNode } from 'react';
import { FileText, type LucideIcon } from 'lucide-react';
import {
  RecordMeta,
  RecordSummaryCard,
  type RegisteredRecordTypeDefinition,
} from '@command-center/ui-system';
import { formTriageClassificationLabel } from '@oneup/shared';
import {
  CalloutBlock,
  EmailLink,
  FieldRows,
  PhoneLink,
  RecordAvatar,
  RecordPill,
  SectionHeading,
  SpecBox,
  UrlLink,
  formatDateLabel,
  formatDateTimeLabel,
} from './record-presentation';

export interface FormSubmissionView {
  id: string;
  entryId?: string;
  formName?: string;
  siteName?: string;
  submittedAt?: string;
  pageUrl?: string;
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  message?: string;
  summary?: string;
  category?: string;
  categoryLabel?: string;
  /** Intelligent Form Triage: likely_service_request | other_business | needs_review | low_value. */
  classification?: string;
  classificationLabel?: string;
  classificationExplanation?: string;
  classifiedAt?: string;
  reviewStatus?: string;
  fields?: Record<string, unknown>;
  archived?: boolean;
}

/**
 * Historical disposition labels, for rendering an OLD value as history.
 *
 * `review_status` is the deprecated compatibility mirror of `intake_items` and
 * must never drive a current-state display — a host portal passes the derived
 * lifecycle in as `badges` instead. This stays exported so a timeline or an
 * export can still put a human word on a stored value from before the mirror
 * was deprecated.
 */
const REVIEW_LABELS: Record<string, string> = {
  needs_review: 'Needs review',
  lead_created: 'Lead created',
  linked_to_existing_lead: 'Linked to lead',
  current_client_project: 'Current client',
  not_qualified: 'Not qualified',
  spam: 'Spam',
  do_not_contact: 'Do not contact',
  duplicate: 'Duplicate',
  needs_more_information: 'Needs more info',
};

export function reviewStatusLabel(status: string | undefined): string | undefined {
  if (!status) return undefined;
  return REVIEW_LABELS[status] ?? status;
}

/**
 * The one classification badge. It is the only pill with colour on a card, so a
 * reader never has to work out which of several labels is the decision — the
 * secondary form-type label stays on the detail record.
 */
export function FormClassificationPill({ submission }: { submission: FormSubmissionView }) {
  if (!submission.classification) return null;
  const label =
    submission.classificationLabel ?? formTriageClassificationLabel(submission.classification);
  // Three tones for four values, because the reader is answering one question:
  // is this the work? Likely Service Request carries colour, Low Value recedes,
  // and Other Business and Needs Review sit legibly between them — colouring
  // three of four values would leave the badge saying nothing at a glance.
  const tone =
    submission.classification === 'likely_service_request'
      ? 'primary'
      : submission.classification === 'low_value'
        ? 'muted'
        : 'outline';
  return <RecordPill tone={tone}>{label}</RecordPill>;
}

/** Human-readable version of a submitted label ("project-notes", "name first"). */
function humanize(label: string): string {
  const spaced = label.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Turn preserved JSON form values into readable text without assuming every
 * field is a scalar. Historical Gravity Forms entries can store compound name
 * fields as `{ first, last }`; calling `.trim()` on that object used to crash
 * the entire record detail view.
 */
export function formatFormFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(formatFormFieldValue).filter(Boolean).join(', ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const first = formatFormFieldValue(record.first);
    const last = formatFormFieldValue(record.last);
    if (first || last) return [first, last].filter(Boolean).join(' ');

    return Object.entries(record)
      .map(([label, nested]) => {
        const formatted = formatFormFieldValue(nested);
        return formatted ? `${humanize(label)}: ${formatted}` : '';
      })
      .filter(Boolean)
      .join(', ');
  }
  return '';
}

/* ------------------------------------------------------------------- list card */

/**
 * A form entry in a feed.
 *
 * Built on the shared `RecordSummaryCard`, so it reads the same way a Call card
 * and a Lead card do: type strip, name, state, contact details, quiet context,
 * then what they wrote. It previously led with an avatar and a row of four
 * pills — site, form, classification, review status — which put the reader's
 * first question ("has anyone dealt with this?") fourth.
 *
 * Two badges at most: where this stands in its lifecycle, and the triage
 * classification. Site, form, and archived state moved to the meta row, which
 * is where facts that do not require a decision belong.
 */
export function FormSubmissionCard({
  submission,
  selected,
  onSelect,
  badges,
  selection,
  recordTypeDefinition,
  recordTypeIcon,
}: {
  submission: FormSubmissionView;
  selected?: boolean;
  onSelect?: () => void;
  /**
   * Lifecycle badges from the host portal. Optional: a portal without an intake
   * layer has nothing to say here and gets the classification alone.
   */
  badges?: ReactNode;
  recordTypeDefinition?: RegisteredRecordTypeDefinition;
  recordTypeIcon?: LucideIcon;
  selection?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  };
}) {
  return (
    <RecordSummaryCard
      recordType="form_entry"
      recordTypeDefinition={recordTypeDefinition}
      recordTypeIcon={recordTypeIcon}
      title={submission.name ?? 'No name submitted'}
      onOpen={() => onSelect?.()}
      selected={selected}
      stripContext={submission.archived ? 'Archived' : 'Active'}
      selection={
        selection
          ? {
              ...selection,
              label: `Select ${submission.name ?? 'form entry'}`,
            }
          : undefined
      }
      badges={
        <>
          {badges}
          <FormClassificationPill submission={submission} />
        </>
      }
      phone={submission.phone}
      email={submission.email}
      meta={
        <>
          <RecordMeta icon={FileText}>{submission.formName}</RecordMeta>
          <RecordMeta>{submission.siteName}</RecordMeta>
        </>
      }
      summary={submission.summary ?? submission.message}
      footerMeta={`Submitted ${formatDateLabel(submission.submittedAt) ?? 'date unavailable'}`}
    />
  );
}

/** Condensed row for the split-view list beside an open record. */
export function FormSubmissionListItem({
  submission,
  selected,
  onSelect,
}: {
  submission: FormSubmissionView;
  selected?: boolean;
  onSelect?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${
        selected ? 'bg-selection' : 'hover:bg-muted/70'
      }`}
    >
      <RecordAvatar name={submission.name} size="sm" />
      <span className="min-w-0 flex-1">
        <span
          className={`block truncate text-[13px] font-medium ${selected ? 'text-primary' : ''}`}
        >
          {submission.name ?? 'No name submitted'}
        </span>
        {(submission.classification || submission.formName) && (
          <span className="mt-1 flex flex-wrap gap-1">
            <FormClassificationPill submission={submission} />
            {submission.formName && <RecordPill>{submission.formName}</RecordPill>}
            {submission.archived && <RecordPill tone="muted">Archived</RecordPill>}
          </span>
        )}
        <span className="mt-1 block truncate text-[11px] text-muted-foreground">
          {[formatDateLabel(submission.submittedAt), submission.email].filter(Boolean).join('  ')}
        </span>
      </span>
    </button>
  );
}

/* ----------------------------------------------------------------- detail view */

/**
 * Form entry detail.
 *
 * Overview is the whole submission, top to bottom: the facts about it, who sent
 * it and how to reach them, what they said, and finally the raw field-by-field
 * data. The Classification section that used to sit at the top is gone; the
 * classification is a badge in the header, and the sentence explaining it moved
 * to the timeline along with the rest of the analysis reasoning. A reviewer
 * reading a form entry is answering "what is this and who do I call", not
 * auditing the classifier.
 *
 * Contact sits inside the facts box rather than beside it: submitted-at, form,
 * site, name, email, and phone are all the same kind of thing — one-line facts
 * you scan — and splitting them across two containers made the reader look in
 * two places for one answer.
 *
 * Submission Data used to be its own tab. It is reference material, but putting
 * it behind a tab meant the answer to "what did they actually write" was one
 * click away on every single entry. It now sits last on the Overview, below
 * everything summarised, where it is available without being in the way.
 */
export function FormSubmissionDetail({
  submission,
  actions,
  overviewBanner,
  actionCenter,
  contact,
  activity,
  activityLabel = 'Activity & Notes',
  allFields,
  recordManagement,
  recordManagementPosition = 'top',
}: {
  submission: FormSubmissionView;
  actions?: ReactNode;
  overviewBanner?: ReactNode;
  /**
   * Where a reviewer says what happened and what should happen next. Sits
   * directly under the banner that states where this entry stands, so reading
   * the situation and answering it are one movement down the page.
   */
  actionCenter?: ReactNode;
  /** Confirmed business Contact relationship, including an explicit empty state. */
  contact?: ReactNode;
  /** Timeline and notes — what ran, what it decided, and what people have said. */
  activity?: ReactNode;
  activityLabel?: string;
  /** Registry-driven field reference supplied by the host portal. */
  allFields?: ReactNode;
  recordManagement?: ReactNode;
  recordManagementPosition?: 'top' | 'bottom';
}) {
  const entries = Object.entries(submission.fields ?? {})
    .map(([label, value]) => [label, formatFormFieldValue(value)] as const)
    .filter(([, value]) => Boolean(value));
  type DetailTab = 'overview' | 'contact' | 'activity' | 'all-fields';
  const [tab, setTab] = useState<DetailTab>('overview');
  const tabs: Array<{ id: DetailTab; label: string; show: boolean }> = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'contact', label: 'Contact', show: Boolean(contact) },
    { id: 'activity', label: activityLabel, show: Boolean(activity) },
    { id: 'all-fields', label: 'All Fields', show: Boolean(allFields) },
  ];
  const visible = tabs.filter((entry) => entry.show);

  return (
    <div className="flex h-full flex-col">
      <header className="border-b p-4">
        <div className="flex items-start gap-3">
          <RecordAvatar name={submission.name} icon={<FileText className="h-4 w-4" />} />
          <div className="min-w-0 flex-1">
            <h2 className={`break-words text-lg font-semibold leading-snug ${submission.name ? '' : 'text-muted-foreground'}`}>
              {submission.name ?? 'No name submitted'}
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <FormClassificationPill submission={submission} />
              {submission.siteName && <RecordPill>{submission.siteName}</RecordPill>}
              {submission.formName && <RecordPill>{submission.formName}</RecordPill>}
              {submission.archived && <RecordPill tone="muted">Archived</RecordPill>}
              <span className="text-xs text-muted-foreground">
                {formatDateLabel(submission.submittedAt)}
              </span>
            </div>
          </div>
          {/*
            No review pill here. Where an entry stands is stated once, by the
            host portal's status banner at the top of the Overview, which also
            links to the Lead when there is one. A pill repeating a shortened
            version of that sentence in the header was the second answer to a
            question already answered below it.
          */}
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      </header>

      {visible.length > 1 && (
        <nav className="flex gap-1 border-b px-3" role="tablist">
          {visible.map((entry) => (
            <button
              key={entry.id}
              role="tab"
              aria-selected={tab === entry.id}
              type="button"
              onClick={() => setTab(entry.id)}
              className={`px-3 py-2 text-sm transition-colors ${
                tab === entry.id
                  ? 'border-b-2 border-primary font-medium'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      )}

      <div className="flex-1 space-y-6 overflow-y-auto p-4">
        {tab === 'overview' ? (
          <>
            {overviewBanner}
            {actionCenter}
            {recordManagementPosition === 'top' && recordManagement}

            {/*
              One facts box. Submission metadata and contact details are the
              same kind of thing — short labelled facts — so they belong in one
              container the reader scans once, not two they scan in turn.
            */}
            <SpecBox
              items={[
                { label: 'Submitted', value: formatDateTimeLabel(submission.submittedAt) },
                { label: 'Form', value: submission.formName },
                // The narrower form type lives here rather than on the card, where
                // it would read as a second, competing classification.
                { label: 'Form type', value: submission.categoryLabel },
                { label: 'Site', value: submission.siteName },
                { label: 'Name', value: submission.name },
                { label: 'Email', value: <EmailLink value={submission.email} /> },
                { label: 'Phone', value: <PhoneLink value={submission.phone} /> },
                { label: 'Company', value: submission.company },
                { label: 'Page', value: <UrlLink value={submission.pageUrl} /> },
                { label: 'Entry ID', value: submission.entryId },
              ]}
            />

            {submission.summary && (
              <section>
                <SectionHeading>Summary</SectionHeading>
                <CalloutBlock>{submission.summary}</CalloutBlock>
              </section>
            )}

            {submission.message && (
              <section>
                <SectionHeading>Message</SectionHeading>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{submission.message}</p>
              </section>
            )}

            {entries.length > 0 && (
              <section>
                <SectionHeading>Submission Data</SectionHeading>
                <FieldRows
                  rows={entries.map(([label, value]) => ({
                    label: humanize(label),
                    // Contact values stay actionable wherever they appear.
                    value:
                      /mail/i.test(label) && value.includes('@') ? (
                        <EmailLink value={value} />
                      ) : /phone|mobile|cell|tel/i.test(label) ? (
                        <PhoneLink value={value} />
                      ) : /^https?:\/\//i.test(value) ? (
                        <UrlLink value={value} />
                      ) : (
                        value
                      ),
                  }))}
                />
              </section>
            )}
          </>
        ) : tab === 'contact' ? (
          contact
        ) : tab === 'all-fields' ? (
          allFields
        ) : (
          activity
        )}
        {recordManagementPosition === 'bottom' && recordManagement}
      </div>
    </div>
  );
}
